import {useEffect, useRef} from 'react';
import {NovaPromptState, PromptTarget} from '../state/useNovaPromptState';

// Arriving on a Prompt tab from the agent.md tab restores whichever of the System prompt or User
// prompt template the author had last focused there, so a variable clicked in the library before
// either editor is refocused lands back where they were working rather than always in the User
// template. Arriving with the System or User target already active — e.g. stepping away to the Deploy
// tab and back — leaves it alone instead, since there is nothing foreign to reclaim. Only the claim
// itself resets the caret to end-of-input (the editor remounts unfocused either way, so a stale caret
// from a prior visit is never visible); leaving the target alone leaves the caret alone too.
export function usePromptTabTargetClaim({activeTarget, lastPromptTarget, setActiveTarget}: NovaPromptState): void {
	// Captured once, at mount, rather than read as a plain dependency: this only needs to ask "did
	// this tab's input already own the insertion target when it appeared", never "does it own it
	// right now" — the latter would re-run the check on every focus change within this tab (e.g.
	// switching between the two editors here) and risk fighting a target the author just set.
	const arrivingTargetRef = useRef(activeTarget);
	// Also captured once, at mount, for the same reason: the claim fires only on arrival, so it must
	// use whichever editor was last used as of that arrival, not whatever it drifts to afterward while
	// this tab stays mounted.
	const lastPromptTargetRef = useRef(lastPromptTarget);

	useEffect(() => {
		if (arrivingTargetRef.current === PromptTarget.AgentMd) {
			setActiveTarget(lastPromptTargetRef.current);
		}
	}, [setActiveTarget]);
}

// Arriving on an agent.md tab from a Prompt tab makes agent.md the insertion target, so a variable
// clicked in the library before the editor is ever focused lands here rather than in the Prompt tab's
// last active input. The tab owns only one target, so the only time it is already active is a remount
// that isn't a genuine arrival from elsewhere (e.g. the Deploy tab in between); then there is nothing
// to claim. Claiming also resets the insertion point to end-of-input, since the editor remounts
// unfocused either way and any caret from an earlier visit is no longer visible to the author.
export function useAgentMdTargetClaim({activeTarget, setActiveTarget}: NovaPromptState): void {
	const arrivingTargetRef = useRef(activeTarget);

	useEffect(() => {
		if (arrivingTargetRef.current === PromptTarget.System || arrivingTargetRef.current === PromptTarget.User) {
			setActiveTarget(PromptTarget.AgentMd);
		}
	}, [setActiveTarget]);
}
